import { useState } from "react";
import { observer } from "mobx-react-lite";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SettingsSectionLabel } from "@/components/settings/SettingsSectionLabel";
import { OrganizationLogoSection } from "@/components/settings/OrganizationLogoSection";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { toast } from "sonner";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useOrganizationStore } from "@/hooks/useOrganizationStore";
import { Loader2 } from "lucide-react";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

// The page-level Save button sits below the logo section, outside the form, and
// links back to it via the `form` attribute — one Save for the whole page rather
// than a button inside each section.
const ORG_SETTINGS_FORM_ID = "organization-settings-form";

export const OrganizationSettingsForm = observer(function OrganizationSettingsForm() {
  const { t } = useLanguage();
  const { currentOrganization, refetch } = useOrganizationContext();
  const organizationStore = useOrganizationStore();

  const [orgLoading, setOrgLoading] = useState(false);
  const [orgName, setOrgName] = useState(currentOrganization?.name || "");

  const handleOrgSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization) return;

    setOrgLoading(true);
    try {
      const success = await organizationStore.updateOrganization(currentOrganization.id, {
        name: orgName,
      });

      if (!success) {
        throw new Error(organizationStore.error || "Failed to update organization");
      }
      // Refresh the org context so the new name reaches the switcher, not just this form.
      await refetch();
      toast.success(t.common.settingsSaved);
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error saving organization settings:", error);
      }
      toast.error(t.common.errorSavingSettings);
    } finally {
      setOrgLoading(false);
    }
  };

  // Cancel discards unsaved edits by re-seeding from the current org name.
  const resetName = () => setOrgName(currentOrganization?.name || "");

  const disabled = orgLoading || !currentOrganization;

  return (
    <div>
      {/* Sections sit flat on the page background, each introduced by an
          uppercase label and separated by a hairline. */}
      <form id={ORG_SETTINGS_FORM_ID} onSubmit={handleOrgSave} className="divide-y divide-border">
        <section className="space-y-4 pb-8">
          <SettingsSectionLabel>{t.settings.organization.details}</SettingsSectionLabel>
          <div className="max-w-md space-y-2">
            <Label htmlFor="orgName">{t.common.organizationName}</Label>
            <Input
              id="orgName"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              disabled={disabled}
            />
          </div>
        </section>

        {/* Logo upload/remove apply immediately (like the profile picture), so
            the section renders in the flat stack but has no Save button of its
            own — the page-level Save below covers the whole page. */}
        <OrganizationLogoSection />
      </form>

      <div className="flex items-center justify-end gap-3 border-t pt-6">
        <Button type="button" variant="ghost" onClick={resetName} disabled={disabled}>
          {t.common.cancel}
        </Button>
        <Button
          type="submit"
          form={ORG_SETTINGS_FORM_ID}
          disabled={disabled}
          {...pageInfoTarget("org.save")}
        >
          {orgLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t.common.saving}
            </>
          ) : (
            t.common.saveChanges
          )}
        </Button>
      </div>
    </div>
  );
});
